import React, { useState, useRef, useEffect } from 'react';

/**
 * CompletionRing — a small SVG circular progress ring used in list views to
 * surface profile-completion at a glance.
 *
 * Props:
 *   percent  number   0–100
 *   missing  string[] list of missing applicable field labels (for tooltip)
 *   size     number   pixel diameter (default 36)
 *   stroke   number   ring stroke width (default 4)
 *   onClick  fn       optional click handler (navigate to details)
 *
 * Color logic: <50% red, 50–85% amber, >85% green.
 */
const CompletionRing = ({ percent = 0, missing = [], size = 36, stroke = 4, onClick, testId }) => {
  const [hovered, setHovered] = useState(false);
  const wrapRef = useRef(null);
  const [coords, setCoords] = useState({ x: 0, y: 0 });

  const pct = Math.max(0, Math.min(100, Math.round(percent || 0)));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  let color = '#ef4444'; // red
  if (pct >= 85) color = '#16a34a'; // green
  else if (pct >= 50) color = '#f59e0b'; // amber

  // Compute tooltip position (above the ring, clamped to viewport)
  useEffect(() => {
    if (!hovered || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setCoords({
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  }, [hovered]);

  const tooltipText = pct === 100
    ? 'All applicable fields filled'
    : `${missing.length} field${missing.length !== 1 ? 's' : ''} missing`;

  return (
    <span
      ref={wrapRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        verticalAlign: 'middle',
      }}
      data-testid={testId || `completion-ring-${pct}`}
      title={tooltipText}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          fontSize={size * 0.32}
          fontWeight="600"
          fill={color}
          fontFamily="ui-sans-serif, system-ui, -apple-system, sans-serif"
        >
          {pct}%
        </text>
      </svg>
      {hovered && (
        <span
          role="tooltip"
          data-testid="completion-ring-tooltip"
          style={{
            position: 'fixed',
            left: coords.x,
            top: coords.y - 8,
            transform: 'translate(-50%, -100%)',
            background: 'rgba(17, 24, 39, 0.96)',
            color: '#fff',
            padding: '8px 10px',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.4,
            maxWidth: 260,
            minWidth: 180,
            zIndex: 50,
            boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
            pointerEvents: 'none',
            whiteSpace: 'normal',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: missing.length ? 4 : 0 }}>{pct}% complete</div>
          {missing.length === 0 ? (
            <div style={{ opacity: 0.8 }}>All applicable fields filled.</div>
          ) : (
            <>
              <div style={{ opacity: 0.85, marginBottom: 3 }}>Missing ({missing.length}):</div>
              <ul style={{ margin: 0, padding: '0 0 0 14px', listStyle: 'disc' }}>
                {missing.slice(0, 12).map((m, i) => (
                  <li key={i} style={{ opacity: 0.95 }}>{m}</li>
                ))}
                {missing.length > 12 && (
                  <li style={{ opacity: 0.7 }}>+ {missing.length - 12} more</li>
                )}
              </ul>
            </>
          )}
        </span>
      )}
    </span>
  );
};

export default CompletionRing;
